import Rating from "@components/rating/starRating";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAltRounded";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { reviewLikes } from "services/dashboard";
import { Review } from "stores/useStore/useReviews";

const LikeButton = styled("button")<{ like: "on" | "off" }>(
  ({ theme, like = "off" }) => ({
    display: "inline-flex",
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 8,
    paddingBottom: 8,
    alignItems: "center",
    borderRadius: 8,
    cursor: "pointer",
    border: "0px",
    background:
      like === "off" ? theme.palette.grey[300] : theme.palette.primary.main,
    color:
      like === "off" ? theme.palette.text.primary : theme.palette.common.white,
  })
);

type Props = {
  review: Review;
  likes: string[];
  afterLike: (id: string) => void;
};

export default function Reviews({
  review,
  likes,
  afterLike,
}: Props): React.ReactElement {
  const [loading, setLoading] = React.useState(false);
  const isLike = React.useMemo(() => {
    if (likes.find((item) => item === review.id)) {
      return true;
    }
    return false;
  }, [likes, review]);

  const handleLike = () => {
    setLoading(true);
    reviewLikes({
      reviewId: review.id,
    })
      .then((res) => {
        afterLike(review.id);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Rating defaultValue={review.score} readOnly />
      <Box sx={{ mt: 0.5 }}>
        <b>By {review.name}</b> <span>at {review.date}</span>
      </Box>
      <Box sx={{ my: 2 }}>
        <Typography variant="body1"></Typography>
        <Box
          dangerouslySetInnerHTML={{ __html: review.content }}
          sx={{ fontSize: (theme) => theme.typography.body1.fontSize }}
        ></Box>
      </Box>
      {review?.images?.length > 0 && (
        <Grid container spacing={2} sx={{ my: 2 }} direction="row">
          {review.images.map((image, index) => {
            return (
              <Grid item xs={2} key={index}>
                <img src={image.src} style={{ width: "100%" }} />
              </Grid>
            );
          })}
        </Grid>
      )}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <LikeButton
          like={isLike ? "on" : "off"}
          onClick={handleLike}
          disabled={loading || isLike}
        >
          <ThumbUpAltIcon />
        </LikeButton>
        {review.likes > 0 && (
          <React.Fragment>
            <Divider orientation="vertical" flexItem sx={{ mx: 2, my: 0.5 }} />
            <Box sx={{ color: (theme) => theme.palette.text.secondary }}>
              <Box
                component="b"
                sx={{ color: (theme) => theme.palette.text.primary }}
              >
                {review.likes}
              </Box>
              &nbsp;&nbsp;person found this review helpful
            </Box>
          </React.Fragment>
        )}
      </Box>
    </Box>
  );
}
